import { S3Client, GetObjectTaggingCommand, PutObjectTaggingCommand, DeleteObjectCommand } from '@aws-sdk/client-s3';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';

/**
 * S3 Service - Singleton pattern for S3 operations
 * Uses Amplify's fetchAuthSession() which automatically handles:
 * - Cognito credential refresh via refresh tokens
 * - Token expiration
 * - Re-authentication when needed
 * 
 * This ensures credentials are always valid without manual management.
 */
class S3Service {
  constructor() {
    this.client = null;
  }

  /**
   * Get S3 client with fresh credentials
   * Amplify's fetchAuthSession() automatically refreshes expired credentials
   */
  async getClient() {
    // Always get fresh session from Amplify - it handles caching and refresh internally
    const session = await fetchAuthSession();
    
    // Create new client with current credentials
    // Amplify ensures these are always valid
    this.client = new S3Client({
      region: config.Region,
      credentials: session.credentials
    });
    
    return this.client;
  }

  /**
   * Get tags for an S3 object
   */
  async getTags(bucket, key) {
    const client = await this.getClient();
    
    try {
      const response = await client.send(new GetObjectTaggingCommand({
        Bucket: bucket,
        Key: key
      }));
      
      const tags = {};
      response.TagSet?.forEach(tag => {
        tags[tag.Key] = tag.Value;
      });
      
      return tags;
    } catch (err) {
      console.error(`Error fetching tags for ${key}:`, err);
      return {};
    }
  }

  /**
   * Update tags for an S3 object
   */
  async updateTags(bucket, key, tagsToUpdate) {
    const client = await this.getClient();
    
    try {
      // Get existing tags
      const existingTags = await client.send(new GetObjectTaggingCommand({
        Bucket: bucket,
        Key: key
      }));
      
      // Merge with new tags
      const tagMap = {};
      existingTags.TagSet?.forEach(tag => {
        tagMap[tag.Key] = tag.Value;
      });
      
      Object.assign(tagMap, tagsToUpdate);
      
      // Convert back to TagSet format
      const tagSet = Object.entries(tagMap).map(([Key, Value]) => ({ Key, Value }));
      
      // Update tags
      await client.send(new PutObjectTaggingCommand({
        Bucket: bucket,
        Key: key,
        Tagging: { TagSet: tagSet }
      }));
      
      return true;
    } catch (err) {
      console.error(`Error updating tags for ${key}:`, err);
      return false;
    }
  }

  /**
   * Delete an S3 object
   */
  async deleteObject(bucket, key) {
    const client = await this.getClient();
    
    try {
      await client.send(new DeleteObjectCommand({
        Bucket: bucket,
        Key: key
      }));
      return true;
    } catch (err) {
      console.error(`Error deleting ${key}:`, err);
      return false;
    }
  }

  /**
   * Delete multiple S3 objects
   */
  async deleteObjects(bucket, keys) {
    const results = await Promise.allSettled(
      keys.map(key => this.deleteObject(bucket, key))
    );
    
    return results.every(r => r.status === 'fulfilled' && r.value === true);
  }
}

// Export singleton instance
export default new S3Service();
